import { BaseViewStore } from 'core/stores';
import FulfillmentOrderProcessingStore from './FulfillmentOrderProcessingStore';
import FulfillmentOrderStore from './FulfillmentOrderStore';
import { MarkAsShippedBulkForm, MarkAsShippedLtlBulkForm } from 'modules/order/forms';
import { action, makeObservable } from 'mobx';
import { ApplicationStore } from 'platform/application/stores';
import { notificationError, translate } from 'core/utils';
import { CarrierServiceStore, CarrierStore } from 'modules/carrier/stores';
import { carrierTypes, sourceOfChange } from 'common/constants';
import { BaasicDropdownViewStore } from 'common/stores';
import _ from 'lodash';

class MarkAsShippedBulkViewStore extends BaseViewStore {
	parentView = null;

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		if (this.modalParams.isLtl) {
			await this.initializeLtl();
		} else {
			await this.initializeNonLtl();
		}
		this.resume();
	};

	@action.bound
	initializeLtl = async () => {
		this.form = new MarkAsShippedLtlBulkForm({
			onSuccess: async () => {
				await this.markAsShippedBulk();
				this.modalParams.success();
			},
		});

		_.each(this.modalParams.data, async item => {
			const field = this.form.$('fulfillmentOrders').add();
			field.update({
				externalShipment: false,
				fulfillmentOrderId: item.fulfillmentOrderId,
				orderNumber: item.orderNumber,
				trackingNumber: item.trackingNumber,
				carrierId: item.carrierId,
				carrierServiceId: item.carrierServiceId,
				id: item.id,
			});
			field.carrierDropdownStore = new BaasicDropdownViewStore();
			await field.carrierDropdownStore.initialize({
				store: new CarrierStore(this.rootStore),
				field: field.$('carrierId'),
				value: item.carrierId && {
					value: item.carrierId,
					label: item.carrier,
				},
				filterProperties: {
					sellerIds: [item.sellerId],
					carrierTypeNames: [carrierTypes.ltl],
				},
			});
		});
	};

	@action.bound
	initializeNonLtl = async () => {
		this.form = new MarkAsShippedBulkForm({
			onSuccess: async () => {
				await this.markAsShippedBulk();
				this.modalParams.success();
			},
		});
		_.each(this.modalParams.data, async item => {
			const field = this.form.$('fulfillmentOrders').add();
			field.update({
				externalShipment: false,
				fulfillmentOrderId: item.fulfillmentOrderId,
				orderNumber: item.orderNumber,
				warehouseId: item.warehouseId,
				trackingNumber: item.trackingNumber,
				carrierServiceId: item.carrierServiceId,
				shippingCost: item.shippingCost,
				shippingCostSurcharge: item.shippingCostSurcharge,
				id: item.id,
			});
			field.carrierServiceDropdownStore = new BaasicDropdownViewStore({
				store: new CarrierServiceStore(this.rootStore),
				field: field.$('carrierServiceId'),
				value: item.carrierServiceId && {
					value: item.carrierServiceId,
					label: item.carrierService,
					extra: { carrierId: item.carrierId },
				},
			});
			field.warehouseDropdownStore = new BaasicDropdownViewStore({
				store: new ApplicationStore(this.rootStore),
				field: field.$('warehouseId'),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				value: item.warehouseId && { value: item.warehouseId, label: item.warehouse },
				filterProperties: {
					warehouseOnly: true,
					isOrganizationActive: true,
				},
			});
		});
	};

	@action.bound
	onExternalCallback = (evt, field) => {
		const rules = evt.currentTarget.checked ? '' : 'required';
		field.each(innerField => {
			innerField.set('rules', rules);
			innerField.resetValidation();
		});

		if (!this.modalParams.isLtl && !evt.currentTarget.checked) {
			field.$('shippingCost').set('rules', 'required|numeric|decimalLength|numericMinValue');
			field.$('shippingCostSurcharge').set('rules', 'numeric|decimalLength|numericMinValue');
		}
	};

	@action.bound
	markAsShippedBulk = async () => {
		const { fulfillmentOrders } = this.form.values();
		const fulfillmentOrderstoBeMarkedAsShipped = [];
		_.forEach(fulfillmentOrders, item => {
			const fulfillmentOrdertoBeMarkedAsShipped = {
				id: item.id,
				trackingNumber: item.externalShipment ? '' : item.trackingNumber,
				cost: item.externalShipment ? null : item.shippingCost,
				shippingCostSurcharge:
					item.externalShipment || item.shippingCostSurcharge == 0 ? 0 : item.shippingCostSurcharge,
				warehouseId: item.externalShipment ? null : item.warehouseId,
				carrierServiceId: item.externalShipment ? null : item.carrierServiceId,
				isInternal: !item.externalShipment,
				carrierId: item.carrierId == undefined ? null : item.carrierId, 
			};
			fulfillmentOrderstoBeMarkedAsShipped.push(fulfillmentOrdertoBeMarkedAsShipped);
		});
		try {
			this.modalParams.close();
			const notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.MARKASSHIPPED'));
			const response = await new FulfillmentOrderProcessingStore(this.rootStore).markAsShippedBulk(
				fulfillmentOrderstoBeMarkedAsShipped,
				sourceOfChange.markAsShippedBulkOrderList
			);
			this.rootStore.notificationStore.dismiss(notificationId);

			// Notifications.
			if (response.failed == 0 && response.successful > 0) {
				// If all is success.
				this.rootStore.notificationStore.success([response.message, { count: response.successful }]);
			} else if (response.failed > 0 && response.successful > 0) {
				// If errors occured.
				this.rootStore.notificationStore.warning([
					response.message,
					{ successful: response.successful, failed: response.failed },
				]);
			} else {
				// If all is fail.
				this.rootStore.notificationStore.error([response.message]);
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	};
}

export default MarkAsShippedBulkViewStore;
